import React, { useState } from 'react';

import { useMiniAppSDK } from '@context/SDKConfigContext.tsx';
import { useSDKHandler } from '@features/method-tester/hooks/index.ts';

import { VERSION_0_3 } from '@/constant';
import { useJsBridgeVersion } from '@/context/JsBridgeVersionContext.tsx';
import { ERROR_CODE_MESSAGE_MAP } from '@/features/method-tester/constants';
import { OPERATION_KEY } from '@/features/method-tester/types/SDKCallResult.ts';
import { Button } from '@asphalt-react/button';
import { Card } from '@asphalt-react/card';
import { Loader } from '@asphalt-react/loader';
import { Textfield } from '@asphalt-react/textfield';
import { Heading } from '@asphalt-react/typography';
import { TriggerPaymentResult } from '@gopay/miniapp-client';

export const Payment = () => {
  const [deeplink, setDeepLink] = useState<string>('gopay://scanqr');
  const {
    isLoading,
    response: paymentResp,
    invokeSDKActionV0_3,
    invokeSDKActionV1,
  } = useSDKHandler<TriggerPaymentResult>();

  const sdk = useMiniAppSDK();
  const { version } = useJsBridgeVersion();
  function onInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    setDeepLink(event.target.value);
  }

  const [selectedErrorCode, setSelectedErrorCode] = useState<number | null>(
    null
  );

  const triggerPayment = async () => {
    try {
      const options = {
        ...(selectedErrorCode && {
          error: {
            operation_key: OPERATION_KEY.PAYMENT,
            code: selectedErrorCode,
          },
        }),
      };
      if (version === VERSION_0_3) {
        await invokeSDKActionV0_3(
          () => sdk.triggerPayment({ deeplink }),
          options
        );
      } else {
        await invokeSDKActionV1('GP', 'launchPayment', { deeplink: deeplink });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const resetSelection = () => setSelectedErrorCode(null);

  return (
    <Card>
      <div className="flex w-full justify-between">
        <Heading h6>Payment</Heading>
        <Button size="xs" onClick={triggerPayment} disabled={isLoading}>
          Trigger payment
        </Button>
      </div>
      <div className="pt-2">
        <Textfield
          value={deeplink}
          placeholder="Enter the Deep Link"
          onChange={onInputChange}
          stretch={true}
          size="s"
        />
      </div>
      <div className="flex gap-4">
        {ERROR_CODE_MESSAGE_MAP.payment.map(({ code, message }) => (
          <label key={code} className="flex items-center gap-1">
            <input
              type="radio"
              name="ERROR_CODE"
              value={code}
              checked={selectedErrorCode === code}
              onChange={() => setSelectedErrorCode(code)}
            />
            {message}
          </label>
        ))}
      </div>
      <Button size="xs" onClick={resetSelection}>
        Reset Error Code
      </Button>
      {isLoading && <Loader />}
      {paymentResp && (
        <div className="mt-2 w-full">
          <pre
            data-testid="payment-response"
            className="overflow-auto text-sm bg-gray-100 p-2 rounded"
          >
            {JSON.stringify(paymentResp, null, 2)}
          </pre>
        </div>
      )}
    </Card>
  );
};
